import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const styles = {
  page: {
    background: '#ffffff',
    color: '#111111',
    minHeight: '100vh',
    fontFamily: 'var(--font-body, Inter, sans-serif)',
  },
  hero: {
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '80px 40px 48px',
    textAlign: 'center',
  },
  heroEyebrow: {
    fontSize: 10,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: 'var(--gold, #c9a96e)',
    fontWeight: 600,
    marginBottom: 16,
  },
  heroTitle: {
    fontFamily: 'var(--font-display, "Cormorant Garamond", serif)',
    fontSize: 'clamp(32px, 5vw, 52px)',
    fontWeight: 300,
    letterSpacing: '0.05em',
    color: '#111111',
    marginBottom: 16,
  },
  heroDate: {
    fontSize: 12,
    color: '#11111',
    letterSpacing: '0.1em',
  },
  container: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '56px 24px 80px',
  },
  section: {
    marginBottom: 48,
  },
  sectionTitle: {
    fontFamily: 'var(--font-display, "Cormorant Garamond", serif)',
    fontSize: 22,
    fontWeight: 400,
    color: '#111111',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    letterSpacing: '0.05em',
  },
  para: {
    fontSize: 14,
    lineHeight: 1.9,
    color: '#111111',
    marginBottom: 14,
  },
  list: {
    paddingLeft: 20,
    marginBottom: 14,
  },
  listItem: {
    fontSize: 14,
    lineHeight: 1.9,
    color: '#111111',
    marginBottom: 8,
  },
  highlight: {
    color: 'var(--gold, #c9a96e)',
    fontWeight: 500,
  },
  tableWrap: {
    overflowX: 'auto',
    marginBottom: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    minWidth: 480,
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)',
    color: '#111111',
    fontWeight: 600,
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  td: {
    padding: '10px 14px',
    color: '#111111',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    verticalAlign: 'top',
    lineHeight: 1.7,
  },
  contactBox: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderLeft: '3px solid var(--gold, #c9a96e)',
    padding: '24px 28px',
    marginTop: 48,
    borderRadius: 2,
  },
  contactTitle: {
    fontSize: 13,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#111111',
    fontWeight: 600,
    marginBottom: 12,
  },
  contactText: {
    fontSize: 13,
    color: '#11111',
    lineHeight: 1.8,
  },
  contactLink: {
    color: 'var(--gold, #c9a96e)',
    textDecoration: 'none',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.4)',
    textDecoration: 'none',
    marginBottom: 40,
    transition: 'color 0.2s',
  },
};

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy | Aurora Luxury India';
  }, []);

  return (
    <div style={styles.page}>
      {/* Hero */}
      <div style={styles.hero}>
        <p style={styles.heroEyebrow}>Legal</p>
        <h1 style={styles.heroTitle}>Privacy Policy</h1>
        <p style={styles.heroDate}>Last updated: June 2025</p>
      </div>

      <div style={styles.container}>
        <Link to="/" style={styles.backLink}
          onMouseEnter={e => e.target.style.color = '#111111'}
          onMouseLeave={e => e.target.style.color = '#111111'}>
          ← Back to Home
        </Link>

        {/* Intro */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.para}>
            Aurora Luxury India ("we", "our", "us") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you visit <span style={styles.highlight}>auroraluxury.shop</span> or interact with us through any channel including Instagram, WhatsApp, or email.
          </p>
          <p style={styles.para}>
            By using our Site, you consent to the practices described in this policy. Please read it carefully.
          </p>
        </div>

        {/* What we collect */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Information We Collect</h2>
          <p style={styles.para}>We collect the following types of information:</p>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Examples</th>
                  <th style={styles.th}>How Collected</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={styles.td}>Personal Details</td>
                  <td style={styles.td}>Name, phone number, email address, delivery address</td>
                  <td style={styles.td}>Order forms, enquiry forms, newsletter signup</td>
                </tr>
                <tr>
                  <td style={styles.td}>Transaction Data</td>
                  <td style={styles.td}>Order history, items purchased, payment status</td>
                  <td style={styles.td}>Checkout process</td>
                </tr>
                <tr>
                  <td style={styles.td}>Usage Data</td>
                  <td style={styles.td}>Pages visited, time spent, clicks, device type</td>
                  <td style={styles.td}>Google Analytics, GTM</td>
                </tr>
                <tr>
                  <td style={styles.td}>Communication Data</td>
                  <td style={styles.td}>Messages sent via WhatsApp, Instagram, or email</td>
                  <td style={styles.td}>Direct communication</td>
                </tr>
                <tr>
                  <td style={styles.td}>Technical Data</td>
                  <td style={styles.td}>IP address, browser type, cookies</td>
                  <td style={styles.td}>Automatic collection on Site visit</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* How we use */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>3. How We Use Your Information</h2>
          <p style={styles.para}>We use the information we collect to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Process and fulfil your orders and send shipping updates.</li>
            <li style={styles.listItem}>Respond to enquiries and provide customer support.</li>
            <li style={styles.listItem}>Send newsletters and promotional offers (only if you opted in).</li>
            <li style={styles.listItem}>Improve our website, products, and user experience.</li>
            <li style={styles.listItem}>Detect and prevent fraud or unauthorised activity.</li>
            <li style={styles.listItem}>Comply with legal obligations under Indian law.</li>
          </ul>
          <p style={styles.para}>
            We will <span style={styles.highlight}>never sell your personal data</span> to third parties for their marketing purposes.
          </p>
        </div>

        {/* Cookies */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Cookies & Tracking</h2>
          <p style={styles.para}>
            Our Site uses cookies and similar tracking technologies to enhance your browsing experience and gather analytics data. These include:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><span style={styles.highlight}>Google Analytics (GA4)</span> — to understand how visitors interact with our Site.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Google Tag Manager</span> — to manage tracking scripts efficiently.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Meta Pixel</span> — to measure the effectiveness of our Instagram and Facebook marketing.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Essential cookies</span> — required for cart, wishlist, and session functionality.</li>
          </ul>
          <p style={styles.para}>
            You can control cookies through your browser settings. Disabling cookies may affect some Site functionality such as the wishlist or cart.
          </p>
        </div>

        {/* Sharing */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>5. Data Sharing</h2>
          <p style={styles.para}>
            We share your data only where necessary with trusted third parties:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}><span style={styles.highlight}>Courier partners</span> — to deliver your order (name, phone, address).</li>
            <li style={styles.listItem}><span style={styles.highlight}>Payment gateways</span> — to process transactions securely. We do not store payment card details.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Analytics platforms</span> — Google Analytics receives anonymised usage data.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Legal authorities</span> — if required by law or to protect our legal rights.</li>
          </ul>
        </div>

        {/* Social Media */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Social Media & Third-Party Platforms</h2>
          <p style={styles.para}>
            Our Site links to and integrates with third-party social media platforms including Instagram and Facebook. If you interact with us on these platforms, your data is also governed by their respective privacy policies.
          </p>
          <p style={styles.para}>
            We use Meta Pixel on our Site to understand how customers who see our Instagram or Facebook ads interact with our website. This helps us show more relevant ads to people. The data collected through Meta Pixel is anonymised and used only for advertising measurement purposes.
          </p>
          <p style={styles.para}>
            Aurora Luxury India does not use your personal data to engage in any deceptive, fraudulent, or misleading practices on any platform. Our social media accounts are managed in full compliance with Instagram and Facebook Community Standards.
          </p>
        </div>

        {/* Data Security */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Data Security</h2>
          <p style={styles.para}>
            We take the security of your personal information seriously. Measures we have in place include:
          </p>
          <ul style={styles.list}>
            <li style={styles.listItem}>SSL/TLS encryption on all pages of our Site (HTTPS).</li>
            <li style={styles.listItem}>Secure, restricted access to databases containing customer information.</li>
            <li style={styles.listItem}>Payment processing handled by PCI-DSS compliant payment gateways — we never store card details.</li>
            <li style={styles.listItem}>Regular review of our data practices and security measures.</li>
          </ul>
          <p style={styles.para}>
            While we take every precaution, no method of transmission over the internet is 100% secure. We encourage you to contact us immediately if you suspect any unauthorised use of your account.
          </p>
        </div>

        {/* Your Rights */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Your Rights</h2>
          <p style={styles.para}>You have the right to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}><span style={styles.highlight}>Access</span> — request a copy of the personal data we hold about you.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Correct</span> — ask us to update any inaccurate data.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Delete</span> — request deletion of your personal data, subject to legal obligations.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Opt out</span> — unsubscribe from marketing communications at any time.</li>
            <li style={styles.listItem}><span style={styles.highlight}>Withdraw consent</span> — for any processing you have previously consented to.</li>
          </ul>
          <p style={styles.para}>
            To exercise any of these rights, contact us at <a href="mailto:support@auroraluxury.shop" style={styles.contactLink}>support@auroraluxury.shop</a>.
          </p>
        </div>

        {/* Retention */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Data Retention</h2>
          <p style={styles.para}>
            We retain your personal data for as long as necessary to fulfil the purposes outlined in this policy, comply with legal requirements, and resolve disputes. Order and transaction data is typically retained for 5 years in accordance with Indian tax laws. Newsletter subscriber data is retained until you unsubscribe.
          </p>
        </div>

        {/* Children */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Children's Privacy</h2>
          <p style={styles.para}>
            Our Site is not directed at individuals under the age of 18. We do not knowingly collect personal data from children. If you believe a child has provided us with their information, please contact us immediately and we will delete it.
          </p>
        </div>

        {/* Governing Law */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Governing Law</h2>
          <p style={styles.para}>
            This Privacy Policy is governed by the laws of India, including the Information Technology Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011. Any disputes shall be subject to the jurisdiction of courts in Hyderabad, Telangana.
          </p>
        </div>

        {/* Contact */}
        <div style={styles.contactBox}>
          <p style={styles.contactTitle}>Privacy Concerns or Requests?</p>
          <p style={styles.contactText}>
            Reach our client services team at{' '}
            <a href="mailto:support@auroraluxury.shop" style={styles.contactLink}>
              support@auroraluxury.shop
            </a>
            <br />
            or via{' '}
            <a href="https://www.instagram.com/aurora_luxury_india" target="_blank" rel="noreferrer" style={styles.contactLink}>
              Instagram DM
            </a>
            . We will respond within 48 hours.
          </p>
        </div>
      </div>
    </div>
  );
}